import { RenderClearFlag } from "../../../RenderEngine/RenderEnum/RenderClearFlag";
import { BaseCamera } from "../../../d3/core/BaseCamera";
import { ShadowMode } from "../../../d3/core/light/ShadowMode";
import { Scene3DShaderDeclaration } from "../../../d3/core/scene/Scene3DShaderDeclaration";
import { ShadowCasterPass } from "../../../d3/shadowMap/ShadowCasterPass";
import { ShadowSpotData } from "../../../d3/shadowMap/ShadowSliceData";
import { LayaGL } from "../../../layagl/LayaGL";
import { StatElement } from "../../../layagl/StatisticsContext";
import { Color } from "../../../maths/Color";
import { MathUtils3D } from "../../../maths/MathUtils3D";
import { Matrix4x4 } from "../../../maths/Matrix4x4";
import { Vector4 } from "../../../maths/Vector4";
import { Viewport } from "../../../maths/Viewport";
import { Browser } from "../../../utils/Browser";
import { RenderCullUtil } from "../../DriverCommon/RenderCullUtil";
import { RenderListQueue } from "../../DriverCommon/RenderListQueue";
export class WebGLSpotLightShadowRP {
    set light(value) {
        this._light = value;
        this._shadowResolution = this._light.shadowResolution;
        this._lightWorldMatrix = this._light.getWorldMatrix(this._lightWorldMatrix);
        this._lightPos = this._light.transform.position;
        this._spotAngle = this._light.spotAngle;
        this._spotRange = this._light.spotRange;
    }
    get light() {
        return this._light;
    }
    constructor() {
        this._shadowSpotMapSize = new Vector4();
        this._shadowSpotMatrices = new Matrix4x4();
        this._renderQueue = new RenderListQueue(false);
        this._shadowSpotData = new ShadowSpotData();
        this._lightWorldMatrix = new Matrix4x4();
        this._shadowBias = new Vector4();
    }
    update(context) {
        var shadowSpotData = this._shadowSpotData;
        this._getSpotLightShadowData(shadowSpotData, this._shadowResolution, this._shadowSpotMatrices, this._shadowSpotMapSize);
    }
    render(context, list, count) {
        let originCameraData = context.cameraData;
        var shaderValues = context.sceneData;
        context.pipelineMode = "ShadowCaster";
        context.setRenderTarget(this.destTarget, RenderClearFlag.Depth);
        var shadowSpotData = this._shadowSpotData;
        this._getShadowBias(shadowSpotData.resolution, this._shadowBias);
        this._setupShadowCasterShaderValues(shaderValues, shadowSpotData, this._shadowBias);
        var time = Browser.now();
        RenderCullUtil.cullSpotShadow(shadowSpotData.cameraCullInfo, list, count, this._renderQueue, context);
        LayaGL.statAgent.recordTimeData(StatElement.T_CullShadow, Browser.now() - time);
        context.cameraData = shadowSpotData.cameraShaderValue;
        context.cameraUpdateMask++;
        ;
        Viewport.TEMP.set(shadowSpotData.offsetX, shadowSpotData.offsetY, shadowSpotData.resolution, shadowSpotData.resolution);
        Vector4.TEMP.setValue(shadowSpotData.offsetX, shadowSpotData.offsetY, shadowSpotData.resolution, shadowSpotData.resolution);
        context.setViewPort(Viewport.TEMP);
        context.setScissor(Vector4.TEMP);
        context.setClearData(RenderClearFlag.Depth, Color.BLACK, 1, 0);
        this._renderQueue.renderQueue(context);
        LayaGL.statAgent.recordCTData(StatElement.CT_ShadowDrawCall, this._renderQueue.elements.length);
        this._applyCasterPassCommandBuffer(context);
        this._applyRenderData(context.sceneData, context.cameraData);
        this._renderQueue._batch.recoverData();
        context.cameraData = originCameraData;
        context.cameraUpdateMask++;
    }
    _getSpotLightShadowData(shadowSpotData, resolution, shadowSpotMatrices, shadowMapSize) {
        var out = shadowSpotData.position = this._lightPos;
        shadowSpotData.resolution = resolution;
        shadowMapSize.setValue(1.0 / resolution, 1.0 / resolution, resolution, resolution);
        shadowSpotData.offsetX = 0;
        shadowSpotData.offsetY = 0;
        var spotWorldMatrix = this._lightWorldMatrix;
        var viewMatrix = shadowSpotData.viewMatrix;
        var projectMatrix = shadowSpotData.projectionMatrix;
        var viewProjectMatrix = shadowSpotData.viewProjectMatrix;
        var BoundFrustum = shadowSpotData.cameraCullInfo.boundFrustum;
        spotWorldMatrix.invert(viewMatrix);
        Matrix4x4.createPerspective(3.1416 * this._spotAngle / 180.0, 1, 0.1, this._spotRange, projectMatrix);
        Matrix4x4.multiply(projectMatrix, viewMatrix, viewProjectMatrix);
        BoundFrustum.matrix = viewProjectMatrix;
        viewProjectMatrix.cloneTo(shadowSpotMatrices);
        shadowSpotData.cameraCullInfo.position = out;
    }
    _getShadowBias(shadowResolution, out) {
        var frustumSize = Math.tan(this._spotAngle * 0.5 * MathUtils3D.Deg2Rad) * this._spotRange;
        var texelSize = frustumSize / shadowResolution;
        var depthBias = -this._light.shadowDepthBias * texelSize;
        var normalBias = -this._light.shadowNormalBias * texelSize;
        if (this._shadowMode == ShadowMode.SoftHigh) {
            const kernelRadius = 2.5;
            depthBias *= kernelRadius;
            normalBias *= kernelRadius;
        }
        out.setValue(depthBias, normalBias, 0.0, 0.0);
    }
    _setupShadowCasterShaderValues(shaderValues, shadowSliceData, shadowBias) {
        shaderValues.setVector(ShadowCasterPass.SHADOW_BIAS, shadowBias);
        var cameraSV = shadowSliceData.cameraShaderValue;
        cameraSV.setMatrix4x4(BaseCamera.VIEWMATRIX, shadowSliceData.viewMatrix);
        cameraSV.setMatrix4x4(BaseCamera.PROJECTMATRIX, shadowSliceData.projectionMatrix);
        cameraSV.setMatrix4x4(BaseCamera.VIEWPROJECTMATRIX, shadowSliceData.viewProjectMatrix);
        shaderValues.setMatrix4x4(BaseCamera.VIEWPROJECTMATRIX, shadowSliceData.viewProjectMatrix);
    }
    _applyCasterPassCommandBuffer(context) {
        if (!this.shadowCasterCommanBuffer || this.shadowCasterCommanBuffer.length == 0)
            return;
        this.shadowCasterCommanBuffer.forEach(function (value) {
            value._apply();
        });
    }
    _applyRenderData(sceneData, cameraData) {
        var spotLight = this._light;
        switch (spotLight.shadowMode) {
            case ShadowMode.Hard:
                sceneData.removeDefine(Scene3DShaderDeclaration.SHADERDEFINE_SHADOW_SPOT_SOFT_SHADOW_HIGH);
                sceneData.removeDefine(Scene3DShaderDeclaration.SHADERDEFINE_SHADOW_SPOT_SOFT_SHADOW_LOW);
                break;
            case ShadowMode.SoftLow:
                sceneData.addDefine(Scene3DShaderDeclaration.SHADERDEFINE_SHADOW_SPOT_SOFT_SHADOW_LOW);
                sceneData.removeDefine(Scene3DShaderDeclaration.SHADERDEFINE_SHADOW_SPOT_SOFT_SHADOW_HIGH);
                break;
            case ShadowMode.SoftHigh:
                sceneData.addDefine(Scene3DShaderDeclaration.SHADERDEFINE_SHADOW_SPOT_SOFT_SHADOW_HIGH);
                sceneData.removeDefine(Scene3DShaderDeclaration.SHADERDEFINE_SHADOW_SPOT_SOFT_SHADOW_LOW);
                break;
        }
        sceneData.setMatrix4x4(ShadowCasterPass.SHADOW_SPOTMATRICES, this._shadowSpotMatrices);
        sceneData.setVector(ShadowCasterPass.SHADOW_SPOTMAP_SIZE, this._shadowSpotMapSize);
    }
    destroy() {
        this._shadowSpotData.destroy();
    }
}
